import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, Package, Target, Wrench } from 'lucide-react';
import type { StoreProfileDetail } from '../../types';
import { CampaignsByAisleTab } from '../tabs/CampaignsByAisleTab';
import { MediaInventoryTab } from '../tabs/MediaInventoryTab';
import { MediaAvailabilityBadge } from '../MediaAvailabilityBadge';
import { ComplianceBadge } from '../ComplianceBadge';
import { ProvenanceBadge } from '../ProvenanceBadge';

interface OperationsAreaProps {
  profile: StoreProfileDetail;
  storeId?: number;
  storeIdParam?: string;
}

/**
 * Operations area: what is running in the store now — campaigns (current,
 * previous, planned), media inventory, and availability/condition issues.
 */
export function OperationsArea({ profile, storeId, storeIdParam }: OperationsAreaProps) {
  const [subTab, setSubTab] = useState('campaigns');

  const campaignsWithIssues = profile.campaigns.filter((c) => c.issues && c.issues.length > 0);
  const problemMedia = profile.mediaLocations.filter(
    (m) => m.availability === 'unavailable' || m.availability === 'damaged' || m.condition === 'poor' || m.condition === 'damaged',
  );
  const issueCount = campaignsWithIssues.length + problemMedia.length;

  return (
    <Tabs value={subTab} onValueChange={setSubTab}>
      <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 mb-4">
        <TabsTrigger value="campaigns" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 text-gray-600">
          <Target className="h-4 w-4 mr-1" aria-hidden />Campaigns
        </TabsTrigger>
        <TabsTrigger value="media" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 text-gray-600">
          <Package className="h-4 w-4 mr-1" aria-hidden />Media inventory
        </TabsTrigger>
        <TabsTrigger value="issues" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 text-gray-600">
          <Wrench className="h-4 w-4 mr-1" aria-hidden />Availability & issues
          {issueCount > 0 && <Badge variant="secondary" className="ml-1.5">{issueCount}</Badge>}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="campaigns">
        <div className="mb-3 flex items-center gap-2 text-sm text-gray-500">
          <ProvenanceBadge provenance={profile.campaigns.length > 0 ? 'live' : 'missing'} />
          <span>
            {profile.currentCampaignCount ?? 0} active · {profile.campaigns.length} placements on record
          </span>
        </div>
        <CampaignsByAisleTab profile={profile} />
      </TabsContent>

      <TabsContent value="media">
        <MediaInventoryTab profile={profile} storeId={storeId} storeIdParam={storeIdParam} />
      </TabsContent>

      <TabsContent value="issues">
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <ProvenanceBadge provenance="derived" />
            <span>Derived from live campaign assignments and media inventory condition.</span>
          </div>

          {issueCount === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Wrench className="h-10 w-10 mx-auto mb-2 opacity-30" aria-hidden />
                No availability or condition issues on record for this store.
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    Campaign issues
                    <Badge variant="secondary">{campaignsWithIssues.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {campaignsWithIssues.length === 0 && (
                    <p className="text-sm text-gray-500">No campaign issues reported.</p>
                  )}
                  {campaignsWithIssues.map((campaign) => (
                    <div key={campaign.id} className="border rounded-lg p-3 text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium">{campaign.campaignName}</p>
                        <ComplianceBadge status={campaign.complianceStatus} />
                      </div>
                      {campaign.issues?.map((issue) => (
                        <p key={issue} className="text-amber-700 text-xs mt-1 flex items-start gap-1">
                          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" aria-hidden />{issue}
                        </p>
                      ))}
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    Media availability & condition
                    <Badge variant="secondary">{problemMedia.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {problemMedia.length === 0 && (
                    <p className="text-sm text-gray-500">All media locations are available and in good condition.</p>
                  )}
                  {problemMedia.map((media) => (
                    <div key={media.id} className="border rounded-lg p-3 text-sm flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium">{media.formatLabel}</p>
                        <p className="text-xs text-gray-500">{media.locationLabel} · condition: {media.condition}</p>
                      </div>
                      <MediaAvailabilityBadge availability={media.availability} />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
}
